import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CloseIcon, EditIcon, SearchIcon } from '@iconicicons/react';
import { ReduxPointSearch } from '../search/ReduxPointSearch';
import * as selectedItemsActions from './selectedItemsSlice';
import { Point } from '../modes/Sidebar';
import {
  useCreateUpdatePointMutation,
  useDeleteNodeMutation,
  useGetMapPointsQuery,
  useGetMapNodesQuery,
  useGetMapEdgesQuery,
  useUpdatePointMutation,
  useGetInterconnectsQuery,
} from '../services/edgeApi';
import { useDelete } from '../helpers/hooks';
import { usePanMap } from '../plotter/usePanMap';
import { PinPoint } from '../points/PinPoint';
import { ErrorBoundary } from '../helpers/ErrorBoundary';
import {
  selectAllNodes,
  selectAllPoints,
  selectInterconnects,
} from '../map/slice/mapSelectors';
import { ReusableModal } from '../modals/ReusableModal';
import { PointForm } from '../points/PointForm';
import { Can } from '../user/Can';
import { useLocation } from 'react-router-dom';

export function ItemSelector(props) {
  const dispatch = useDispatch();
  // props.hideItems hides the SelectedItems from the ItemSelector card itself
  // SelectedItems can be shown independently of the selector card

  // what is currently being selected
  const currentlySelecting = useSelector(
    (state) => state.selectedItems.currentlySelecting[props.itemType],
  );
  // if this component is what is currently being selected, it's "active"
  const active = currentlySelecting?.selectionKey === props.selectionKey;

  // set disabled to true to prevent selecting and removing more items (for contract show, for instance)
  const { disabled } = props;

  const activate = () => {
    dispatch(
      selectedItemsActions.currentlySelectingChanged({
        selectionKey: props.selectionKey,
        multiSelect: props.multiSelect,
        itemType: props.itemType,
        disabled: props.disabled,
        isEditable: props.isEditable,
        isCreatable: props.isCreatable,
        isDeletable: props.isDeletable,
        preventToggle: props.preventToggle,
        overwrite: props.overwrite,
      }),
    );
  };

  useEffect(() => {
    // send startActive for one selector to be activated on render
    if (props.startActive) {
      activate();
    }

    // send preSelect ids to have objects selected on render
    if (props.preSelectIds) {
      dispatch(
        selectedItemsActions.setSelectedObjects({
          selectionKey: props.selectionKey,
          ids: props.preSelectIds,
        }),
      );
    }
  }, []);

  return (
    <>
      <div onClick={() => activate()}>
        {!props.hideItems && (
          <SelectedItems
            selectionKey={props.selectionKey}
            itemType={props.itemType}
            detailed={props.detailed}
            onChange={props.onChange}
            disabled={disabled}
          />
        )}
      </div>
    </>
  );
}

export const SelectedItems = (props) => {
  // get selected point of select type
  const selectedItems = useSelector(
    (state) => state.selectedItems[props.selectionKey],
  );
  // console.log("Selection Key: "+ props.selectionKey)
  // console.log("SELECTED ITEMS " + selectedItems)

  useEffect(() => {
    props.onChange?.(selectedItems);
  }, [selectedItems]);

  if (selectedItems === undefined) {
    throw "ERROR: You are probably calling a selectionKey that isn't initialized in selectedItemsSlice";
  }
  return (
    <div className="list-group list-group-flush">
      {selectedItems.map((itemId) => {
        if (itemId) {
          switch (props.itemType) {
            case 'interconnect':
              return (
                <SelectedInterconnect key={itemId} interconnectId={itemId} />
              );
            case 'edge':
              return (
                <SelectedEdge
                  key={itemId}
                  edgeId={itemId}
                  selectionKey={props.selectionKey}
                  detailed={props.detailed}
                  disabled={props.disabled}
                />
              );
            case 'point':
              return (
                <ErrorBoundary key={itemId}>
                  <SelectedPoint
                    key={itemId}
                    pointId={itemId}
                    selectionKey={props.selectionKey}
                    detailed={props.detailed}
                    disabled={props.disabled}
                  />
                </ErrorBoundary>
              );
            case 'node':
              return (
                <SelectedNode
                  key={itemId}
                  nodeId={itemId}
                  selectionKey={props.selectionKey}
                  detailed={props.detailed}
                  disabled={props.disabled}
                />
              );
            default:
              return `ERROR: Unknown itemType ${props.itemType} in SelectedItems component`;
          }
        }
        return null;
      })}
    </div>
  );
};

const SelectedInterconnect = (props) => {
  useGetInterconnectsQuery();
  const interconnects = useSelector(selectInterconnects);

  const interconnect = interconnects[props.interconnectId];

  if (interconnect) {
    return (
      <div className="px-3 py-2 card">
        Interconnect: {interconnect.name} (ID: {interconnect.id})
      </div>
    );
  }

  return null;
};

function SelectedPoint(props) {
  const [updatePointTrigger] = useUpdatePointMutation();
  const dispatch = useDispatch();
  const location = useLocation();
  const pointData = useSelector(selectAllPoints)[props.pointId];
  const nodeData = useSelector(selectAllNodes)[pointData?.node];
  const allPoints = useSelector(selectAllPoints);

  const [formPointId, setFormPointId] = useState(null);

  const isMapBuilder = location.pathname.includes('/map-builder');

  const removePoint = (pointId) => {
    dispatch(
      selectedItemsActions.itemRemoved({
        selectionKey: props.selectionKey,
        pointId,
      }),
    );
  };

  const updatePoint = async (pointId, patchData) => {
    const baseData = allPoints[pointId];
    try {
      await updatePointTrigger({ ...baseData, ...patchData }).unwrap();
      toast.success('Point updated successfully');
    } catch (error) {
      console.error('Failed to update point:', error);
      toast.error(error?.data?.message ?? 'Failed to update point');
    }
    setFormPointId(null);
  };

  const panMap = usePanMap();

  if (!pointData) {
    return null;
  }

  // this shows the internal point ID of selected points from URL until point data is loaded.  Could also just not show anything until data is loaded.
  return (
    <div className="px-3 py-2 card">
      {pointData ? (
        <>
          <div>
            <span>
              <PinPoint id={props.pointId} />
            </span>
            <small>
              <b>#{pointData.id}</b>
            </small>
            {!pointData.node && (
              <div className="float-end text-danger">NOT MAPPED</div>
            )}
            {nodeData?.lat && nodeData?.lng && (
              <button
                className="float-end m-0 p-0 btn-sm btn btn-primary-oubtline"
                onClick={() => panMap([nodeData.lat, nodeData.lng])}
              >
                <SearchIcon height={24} color="blue" />
              </button>
            )}
          </div>
          <p className="text-center">{pointData.name}</p>
          <span className="d-block">Loc ID: {pointData.loc_id || 'None'}</span>
          {props.detailed && (
            <>
              <span className="d-block">
                TSP: {pointData.tsp_name || 'None'}
              </span>
              <span className="d-block">
                Segment: {pointData.seg_nbr || 'None'}
              </span>
              <span className="d-block">
                Type: {pointData.loc_type_ind || 'None'}
              </span>
              <span className="d-block">
                Flow Direction: {pointData.dir_flo || 'None'}
              </span>
              <span className="d-block">
                Receipt Zone: {pointData.receipt_zone_name || 'None'}
              </span>
              <span className="d-block">
                Delivery Zone: {pointData.delivery_zone_name || 'None'}
              </span>
              {pointData.up_dn_name && pointData.up_dn_loc_name && (
                <span className="d-block">
                  Connects To: {pointData.up_dn_name} -{' '}
                  {pointData.up_dn_loc_name}
                </span>
              )}
            </>
          )}
          <div className="d-flex justify-content-end">
            {isMapBuilder && (
              <Can can="change_point">
                <button
                  className="float-end m-0 p-0 btn-sm btn btn-primary-outline"
                  onClick={() => setFormPointId(props.pointId)}
                >
                  <EditIcon height={24} />
                </button>
              </Can>
            )}
            {!props.disabled && (
              <button
                className="float-end m-0 p-0 btn-sm btn btn-primary-outline"
                onClick={() => removePoint(props.pointId)}
              >
                <CloseIcon height={24} />
              </button>
            )}
          </div>
        </>
      ) : (
        <div>
          Point ID:
          {props.pointId}
        </div>
      )}
      <ReusableModal
        size="lg"
        isOpen={formPointId !== null}
        onClose={() => setFormPointId(null)}
      >
        <h4 className="text-center">Edit Point #{formPointId}</h4>
        <PointForm
          pointId={formPointId}
          onSave={updatePoint}
          onCancel={() => setFormPointId(null)}
        />
      </ReusableModal>
    </div>
  );
}

function SelectedEdge(props) {
  const dispatch = useDispatch();
  const edgeData = useSelector((state) => state.plotter.edges[props.edgeId]);
  const removeItem = (pointId) => {
    dispatch(
      selectedItemsActions.itemRemoved({
        selectionKey: props.selectionKey,
        pointId,
      }),
    );
  };

  // const [isEditing, setIsEdting] = useState(true)

  return (
    <div className="px-3 py-2 card">
      {edgeData ? (
        <div>{edgeData.id}</div>
      ) : (
        <div>
          Edge ID:
          {props.edgeId}
        </div>
      )}
      <button
        onClick={() => removeItem(props.edgeId)}
        className="float-end btn btn-sm  btn-danger"
      >
        X
      </button>
      <ItemSelector
        itemType="node"
        selectionKey="edgeStartNode"
        title="Start Node"
      />
      <ItemSelector
        itemType="node"
        selectionKey="edgeEndNode"
        title="End Node"
      />
    </div>
  );
}

export const SelectedNode = (props) => {
  // might want to make a selector for allnodesbyid
  const nodeData = useSelector(selectAllNodes)[props.nodeId];
  const [updatePoint] = useCreateUpdatePointMutation();
  const [deleteNode] = useDeleteNodeMutation();
  const { isLoading: pointsLoading } = useGetMapPointsQuery();
  const { isLoading: nodesLoading } = useGetMapNodesQuery();
  const { isLoading: edgesLoading } = useGetMapEdgesQuery();

  const mapIsLoading = pointsLoading || nodesLoading || edgesLoading;

  const removePoint = async (pointId) => {
    // to remove a point from a node, just update the point with null as its node
    const pointData = { id: pointId, node: null };
    try {
      await updatePoint({ point: pointData, node: nodeData });
      toast.success('Point Removed from Node');
    } catch (e) {
      toast.error(JSON.stringify(e));
    }
  };
  const handleDelete = useDelete();
  // const handleDelete = () => {
  //   if (window.confirm(`Are you sure you wish to delete node ${props.nodeId}?`)){
  //     deleteNode(props.nodeId)
  //   }
  // }

  return mapIsLoading ? (
    <div className="px-3 py-2">LOADING</div>
  ) : (
    <div className="px-3 py-2 card">
      {`Node: ${nodeData?.id}`}
      {props.children}
      {nodeData?.points.map((pointId) => (
        <>
          <Point
            key={pointId}
            id={pointId}
            nodeId={props.nodeId}
            detailed={props.detailed}
          />
          {props.isEditable && pointId !== 'new' && (
            <button
              onClick={() => removePoint(pointId)}
              className="btn btn-danger w-100 mt-1"
            >
              Remove Point from Node
            </button>
          )}
        </>
      ))}
      <hr />
      {props.isDeletable && nodeData?.id !== 'new' && (
        <button
          type="button"
          onClick={() =>
            handleDelete(() => deleteNode(props.nodeId), `node ${props.nodeId}`)
          }
          className="btn btn-danger w-100 mt-1"
        >
          Delete Node
        </button>
      )}
    </div>
  );
};
